/**
 * BreadcrumbBar Component
 * Phase 68 M4: 36px breadcrumb bar per locked design 001-global-shell.
 * Renders: [icon 15px] [link] [sep " / "] [current page]
 * Font: 13px, separator: 12px
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { alpha, Box, Typography, useTheme } from '@mui/material'

export interface Breadcrumb {
  label: string
  href?: string
  icon?: React.ReactNode
}

interface BreadcrumbBarProps {
  breadcrumbs: Breadcrumb[]
}

const BreadcrumbBar: React.FC<BreadcrumbBarProps> = ({ breadcrumbs }) => {
  const navigate = useNavigate()
  const theme = useTheme()

  // Always render the bar for layout stability (per notes.md), but empty when no breadcrumbs
  if (breadcrumbs.length === 0) {
    return (
      <Box
        sx={{
          height: 36,
          minHeight: 36,
          borderBottom: '1px solid',
          borderColor: 'divider',
          // Custom crumb-bg per prototype: #f5f3f3 light, #171717 dark
          bgcolor: theme.palette.mode === 'light' ? '#f5f3f3' : '#171717',
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        height: 36,
        minHeight: 36,
        display: 'flex',
        alignItems: 'center',
        px: 2.5, // 20px per prototype
        gap: 0.25, // 2px gap per prototype
        borderBottom: '1px solid',
        // Custom crumb-border per prototype: rgba(0,0,0,0.06) light, rgba(255,255,255,0.04) dark
        borderColor: theme.palette.mode === 'light'
          ? 'rgba(0,0,0,0.06)'
          : 'rgba(255,255,255,0.04)',
        // Custom crumb-bg per prototype
        bgcolor: theme.palette.mode === 'light' ? '#f5f3f3' : '#171717',
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1

        return (
          <React.Fragment key={crumb.label}>
            {/* Separator */}
            {index > 0 && (
              <Typography
                sx={{
                  fontSize: 12, // 12px separator per prototype
                  color: 'text.disabled',
                  mx: 0.5, // 4px each side per prototype
                  userSelect: 'none',
                }}
              >
                /
              </Typography>
            )}

            {/* Icon (only on first crumb typically) */}
            {crumb.icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'info.main', // per prototype: var(--info) blue color
                  mr: 0.75, // 6px per prototype
                  '& svg': { width: 15, height: 15 }, // 15px icon per prototype
                }}
              >
                {crumb.icon}
              </Box>
            )}

            {/* Crumb text */}
            {isLast ? (
              <Typography
                sx={{
                  fontSize: 13, // 13px per prototype
                  fontWeight: 500,
                  color: 'text.primary',
                }}
                aria-current="page"
              >
                {crumb.label}
              </Typography>
            ) : (
              <Typography
                component="span"
                {...(crumb.href
                  ? {
                      role: 'link',
                      tabIndex: 0,
                      onClick: () => navigate(crumb.href!),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(crumb.href!)
                        }
                      },
                    }
                  : {})}
                sx={{
                  fontSize: 13, // 13px per prototype
                  fontWeight: 500,
                  color: 'text.secondary',
                  cursor: crumb.href ? 'pointer' : 'default',
                  px: 0.5, // 4px padding per prototype
                  py: 0.25, // 2px padding
                  borderRadius: 0.5, // 4px per prototype
                  transition: 'color 0.15s ease, background 0.15s ease',
                  '&:hover': crumb.href
                    ? {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.07),
                      }
                    : undefined,
                }}
              >
                {crumb.label}
              </Typography>
            )}
          </React.Fragment>
        )
      })}
    </Box>
  )
}

export default BreadcrumbBar
